// src/annihilation/skilltrees/treeEngine.js
// ============================================================
//  ANNIHILATION — the skill tree engine.
//  Source of truth: ANNIHILATION_GATING_DOCTRINE (PRIME
//  addendum) Parts 2–4, ANNIHILATION_SKILL_TREES (v2) Part 1,
//  ANNIHILATION_SYSTEMS_SPEC (v2) Part 4 (skill points).
//
//  R-UNIVERSAL: this is the ONE gate. Every point spend, every
//  refund, every Affinity commitment — player UI, Trainer NPC,
//  debug tools — funnels through spendNode / refundNode /
//  commitAffinity. No component decides legality inline. Ever.
//
//  DERIVE, DON'T STORE: the save keeps only { spent node ids,
//  affinityId, refundsUsedThisVisit }. Points available, fork
//  locks, cluster totals — all derived here.
//
//  Skill points are scarce and PERMANENT. No full respec at
//  launch. The Trainer refunds ONE point per village visit
//  (two, if Awoken's Self-Mastery is owned — the engine takes
//  the ceiling as input rather than reading Affinity state,
//  so the rule lives in exactly one place: the node itself).
//
//  ⚑⚑ BATCH GATE-1 — THE DEATH OF TIER GATES ⚑⚑
//  The Doctrine (Part 2) removed tier gating entirely. Nothing
//  is forbidden because of a number next to your name. What
//  replaces it, in order of how often it does the work:
//    1. THE AENERGY GATE — you must be able to HOLD it.
//    2. THE POINT COST — you must be willing to BLEED for it.
//    3. THE ENCOUNTER GATE — design-only; the fight asks.
//    4. HERESY — you may force it, and pay forever.
//
//  > THE STEERING LAW. Show the player the optimal move.
//  > Never take the stupid one away from them.
//
//  A Beb can, in principle, stand in the Old Fold wearing an
//  Emperium. It should be the coolest thing in the Plaza.
// ============================================================

import {
  tierForLevel,
  skillPointsAtLevel,
  statsAtLevel,
  aenergyPool,
  AENERGY_IDS,
  AENERGY_LABEL,
  TIERS,
} from "./stats";

// ------------------------------------------------------------
// NODE TYPES — the five markers from Skill Trees v2 Part 1.
// Internal ids never change (vyren pattern); the UI renders
// icons/labels from these ids.
// ------------------------------------------------------------
export const NODE_TYPES = {
  PASSIVE: "passive", //  ○ permanent modifier
  ACTIVE: "active", //    ◆ grants a new ability / skill bar slot
  FORK: "fork", //        ⬘ mutually exclusive with its partner
  CAPSTONE: "capstone", // ★ tree-ending pinnacle
  SCALING: "scaling", //  ◎ grows with level or investment
};

// The four trees. All points spend anywhere — no "main tree."
export const TREE_IDS = {
  LINEAGE: "lineage",
  UNIVERSAL: "universal",
  AFFINITY: "affinity",
  TOME: "tome",
};

// ------------------------------------------------------------
//  ⚑⚑ THE AENERGY PRICING REGISTER (POWER DOCTRINE) ⚑⚑
//
//  GATE-1 established this vocabulary in five nodes and told the
//  next pass to REUSE it rather than re-derive it (roadmap
//  §4.38). Here it is, as one exported table, so that every tree
//  file prices from the same seven rungs instead of inventing a
//  number per node. Read `{ chao: 125 }` as "Champion-hard."
//
//  These are DIALS — retuning a register retunes every node
//  priced at it, everywhere, in one edit. That is the point.
//
//  ⚑ THE TRAIT-PRICING RULE. Price a node in the Aenergy its
//  ability LEANS ON, never the generic pair. Nine Lives at
//  `{ lyt: 8 }` is the pattern to copy. A Lyf-hungry Instinct is
//  priced in Lyf; a Sense in Chao; an Anima-cost U'hlti in
//  Anima. This is Gating Doctrine Part 3 ("which Aenergy gates
//  what") expressed as a pricing habit — and it is the reason a
//  Chaos Dragon reaches different nodes than a Xulan at the same
//  level. That difference IS the game. Use TRAIT() below when a
//  node wants its own stat instead of a register rung.
// ------------------------------------------------------------
export const AENERGY_REGISTER = {
  OPEN: undefined, //                                any Beb holds it
  BEB_HARD: { chao: 60 }, //                         late Beb; you feel it
  CHAMPION_SOFT: { chao: 95 }, //                    ~L25
  CHAMPION_HARD: { chao: 125 }, //                   ~L30
  LORD_SOFT: { chao: 180, anima: 300 }, //           ~L45
  LORD_HARD: { chao: 245, anima: 400 }, //           ~L60
  APEX: { chao: 300, anima: 500, lyf: 4, lyt: 8 }, // Emperium capstones only
};

/** Trait-priced requirement — the exception the Trait-Pricing Rule exists for. */
export const TRAIT = (req) => ({ ...req });

// ------------------------------------------------------------
// SAVE SHAPE — lives inside the character lane
// (profiles/{uid}/annihilation___character/skillTrees).
// Subfield of an already-whitelisted node; flagged in roadmap
// §4.4 as a note, not a new node.
// ------------------------------------------------------------
export const emptyTreeState = () => ({
  spent: {}, //            { [nodeId]: true }
  affinityId: null, //     committed permanently; see AFFINITY_COMMIT_REQ
  refundsUsedThisVisit: 0, // reset on village rest
});

// ============================================================
//  THE AENERGY GATE — ONE HELPER, TWO CALLERS.
//
//  Doctrine Part 3, THE TWO-GATE RULE. Every gated thing is
//  checked twice, against different numbers:
//
//    Gate A — PURCHASE.   Checks the MAX pool. At the Trainer,
//      a Beb whose Chao caps at 60 cannot buy a 200-Chao node,
//      and no amount of hoarded skill points will open it.
//      This is the "level 3 feel," rebuilt.
//
//    Gate B — ACTIVATION. Checks the CURRENT pool. A Lord with
//      200 max Chao and 14 remaining cannot fire their U'hlti.
//      They get to swinging.
//
//  Your ceiling decides what you own; your moment decides what
//  you can use. Both are real decisions, and they are different
//  decisions. `aenergyShortfall` below is the single helper both
//  gates call — canSpendNode passes the max pool, and
//  PlayerController passes the live one.
//
//  THE FLUIDITY LAW: nothing here hardcodes a stat-per-slot
//  table. Requirements live on the node/ability, one field,
//  read by one gate. Some Imperas will cost Anima and no Chao.
// ============================================================

/**
 * @typedef {{ anima?: number, chao?: number, lyf?: number, lyt?: number }} AenergyReq
 * @typedef {{ anima: number, chao: number, lyf: number, lyt: number }} AenergyPool
 */

/**
 * The one Aenergy check. Returns the FIRST unmet requirement, or
 * null if the pool satisfies all of them.
 *
 * A missing pool returns null (gate skipped) rather than locking
 * everything: a caller that forgot to pass a pool should get a
 * game that steers instead of a game that forbids. Callers that
 * care can assert on `aenergyKnown` below.
 *
 * @param {AenergyPool|null} pool
 * @param {AenergyReq|null|undefined} req
 * @returns {{ stat: string, label: string, need: number, have: number }|null}
 */
export function aenergyShortfall(pool, req) {
  if (!req || !pool) return null;
  for (const stat of AENERGY_IDS) {
    const need = req[stat];
    if (need == null) continue;
    const have = pool[stat] ?? 0;
    if (have < need) {
      return { stat, label: AENERGY_LABEL[stat] || stat, need, have };
    }
  }
  return null;
}

/** Boolean form of the same check. */
export const meetsAenergy = (pool, req) => aenergyShortfall(pool, req) == null;

/** Was a real pool supplied? (For callers that want to be strict.) */
export const aenergyKnown = (pool) => Boolean(pool);

/**
 * Human-readable requirement, for tooltips and locked nodes.
 * "200 Chao · 40 Anima" — order follows AENERGY_IDS, always.
 */
export const describeAenergyReq = (req) => {
  if (!req) return "";
  return AENERGY_IDS.filter((s) => req[s] != null)
    .map((s) => `${req[s]} ${AENERGY_LABEL[s]}`)
    .join(" · ");
};

/**
 * Resolve the MAX pool for a spend context. Callers may pass a
 * ready-made `pool`, or a `beingId` (the pool then derives from
 * statsAtLevel — derive-don't-store, one source).
 */
export const poolForContext = ({ pool, beingId, level } = {}) => {
  if (pool) return pool;
  if (beingId && level != null) return aenergyPool(statsAtLevel(beingId, level), level);
  return null;
};

// ------------------------------------------------------------
// DERIVATIONS
// ------------------------------------------------------------

/** Total points spent across all trees. */
export const pointsSpent = (state) => Object.keys(state?.spent || {}).length;

// ------------------------------------------------------------
//  ⚑⚑ RECONCILIATION — THE CUT-SAFETY VALVE (POWER DOCTRINE) ⚑⚑
//
//  Points are SCARCE AND PERMANENT — that is an untouchable
//  design pillar. Which means a node we delete is a point a
//  living save already paid for, sitting in `spent` under an id
//  that no longer resolves to anything. `pointsSpent` would keep
//  counting it forever and `refundNode` could never reach it,
//  because refunding needs a node object that no longer exists.
//  The player would silently, permanently lose a point to a
//  balance decision they had no part in. Unacceptable.
//
//  So: any spent id that no longer exists in the game's node set
//  is dropped, and its point returns to the pool automatically.
//  Run this ONCE on load, before anything reads the tree state.
//
//  Written now for THE CUT (Spectral Tiger 38→27, Universal
//  20→15+5, Tome 15→12) but deliberately GENERAL — the Affinity
//  cut is still held behind the four-vs-eight ruling (§4.40) and
//  will need exactly this same valve when it lands.
//
//  Idempotent. Returns the same object identity when nothing
//  changed, so it is safe to call in a render path.
// ------------------------------------------------------------

/**
 * @param {object} state          the saved tree state
 * @param {Array}  allKnownNodes  every node from every tree this character has
 * @returns {{ state: object, dropped: string[], pointsReturned: number }}
 */
export function reconcileTreeState(state, allKnownNodes = []) {
  const spent = state?.spent || {};
  const known = new Set(allKnownNodes.map((n) => n && n.id).filter(Boolean));
  const dropped = Object.keys(spent).filter((id) => !known.has(id));
  if (dropped.length === 0) return { state, dropped: [], pointsReturned: 0 };

  const next = {};
  for (const id of Object.keys(spent)) if (known.has(id)) next[id] = spent[id];

  // An Affinity committed to an Affinity that no longer exists releases the
  // oath rather than locking the character out of all eight forever.
  return {
    state: { ...state, spent: next },
    dropped,
    pointsReturned: dropped.length,
  };
}

/** Points still available at a level (schedule lives in stats.js). */
export const pointsAvailable = (state, level) =>
  Math.max(0, skillPointsAtLevel(level) - pointsSpent(state));

/** Points spent inside one cluster of one tree (for cluster gates like E1). */
export const pointsInCluster = (state, nodes, clusterId) =>
  nodes.filter((n) => n.cluster === clusterId && state.spent[n.id]).length;

/** Is a node's fork partner already taken? (Picking one locks the other.) */
export const forkLocked = (state, node) =>
  Boolean(node.forkWith && state.spent[node.forkWith]);

// ------------------------------------------------------------
// THE GATE — canSpendNode answers legality; spendNode executes.
// `nodes` is the full node list of the node's own tree (needed
// for prereq + cluster-gate checks). For Affinity nodes, pass
// the committed Affinity's 15 nodes.
//
// ctx: { level, beingId?, pool? }
//   · level    — required (points schedule)
//   · beingId  — lets the engine derive the max Aenergy pool
//   · pool     — a pre-derived pool wins over beingId
// ------------------------------------------------------------

/**
 * @returns {{ ok: boolean, reason?: string, shortfall?: object }}
 */
export function canSpendNode(state, node, nodes, ctx = {}) {
  const { level } = ctx;
  if (!node) return { ok: false, reason: "unknown_node" };
  if (state.spent[node.id]) return { ok: false, reason: "already_owned" };
  if (pointsAvailable(state, level) < (node.cost ?? 1))
    return { ok: false, reason: "no_points" };

  // Fork exclusivity — the identity decision. Permanent.
  if (forkLocked(state, node)) return { ok: false, reason: "fork_locked" };

  // ── THE AENERGY GATE (Gate A — checks the MAX pool) ────────
  // The replacement for tier gating. A ceiling you cannot reach
  // locks the node visibly, with its requirement printed, and no
  // amount of skill points will open it.
  const shortfall = aenergyShortfall(poolForContext(ctx), node.aenergyReq);
  if (shortfall) return { ok: false, reason: "aenergy_locked", shortfall };

  // ── TIER GATE — DORMANT (Doctrine Part 2) ─────────────────
  // `node.tierReq` is DEAD. The check is deliberately kept here,
  // commented, rather than deleted: the tree data files still
  // carry the field, and a dormant check is cheaper than a data
  // migration. Nothing may re-enable this without a caP'n
  // re-decision — there are no tier checks in Annihilation.
  //
  //   if (node.tierReq) {
  //     const order = [TIERS.BEB, TIERS.CHAMPION, TIERS.LORD];
  //     if (order.indexOf(tierForLevel(level)) < order.indexOf(node.tierReq))
  //       return { ok: false, reason: "tier_locked" };
  //   }

  // Node prerequisites (ALL required — e.g. Heart-Seeker requires L5a;
  // Mirage requires Shadow AND Echo).
  for (const req of node.requires || []) {
    if (!state.spent[req]) return { ok: false, reason: "missing_prereq" };
  }

  // Cluster investment gate (e.g. The Ghost Realm requires 5 points
  // already spent in the Haunting Cluster).
  if (node.clusterPointsReq) {
    const { cluster, points } = node.clusterPointsReq;
    if (pointsInCluster(state, nodes, cluster) < points)
      return { ok: false, reason: "cluster_investment" };
  }

  // Affinity nodes are only legal on the committed Affinity.
  if (node.affinityId && node.affinityId !== state.affinityId)
    return { ok: false, reason: "wrong_affinity" };

  return { ok: true };
}

/** Execute a spend. Returns a NEW state or throws never — check `ok`. */
export function spendNode(state, node, nodes, ctx) {
  const check = canSpendNode(state, node, nodes, ctx);
  if (!check.ok) return { ok: false, reason: check.reason, shortfall: check.shortfall, state };
  return {
    ok: true,
    state: { ...state, spent: { ...state.spent, [node.id]: true } },
  };
}

// ------------------------------------------------------------
// REFUNDS — the Trainer. One per village visit at launch.
// `maxRefunds` defaults to 1; Awoken's Self-Mastery node passes 2.
// A refunded node also releases its fork lock (you un-chose).
// Refunding a node that others depend on is illegal — the tree
// never enters an invalid state.
// ------------------------------------------------------------
export function canRefundNode(state, node, nodes, { maxRefunds = 1 } = {}) {
  if (!node || !state.spent[node.id]) return { ok: false, reason: "not_owned" };
  if (state.refundsUsedThisVisit >= maxRefunds)
    return { ok: false, reason: "refund_spent" };
  // Nothing owned may depend on it.
  const dependent = nodes.find(
    (n) => state.spent[n.id] && (n.requires || []).includes(node.id)
  );
  if (dependent) return { ok: false, reason: "has_dependents" };
  // Cluster gates must survive the removal (e.g. can't refund a
  // Haunting node below 5 if The Ghost Realm is owned).
  for (const n of nodes) {
    if (!state.spent[n.id] || !n.clusterPointsReq) continue;
    const { cluster, points } = n.clusterPointsReq;
    const after =
      pointsInCluster(state, nodes, cluster) - (node.cluster === cluster ? 1 : 0);
    if (after < points) return { ok: false, reason: "cluster_gate_break" };
  }
  return { ok: true };
}

export function refundNode(state, node, nodes, opts) {
  const check = canRefundNode(state, node, nodes, opts);
  if (!check.ok) return { ok: false, reason: check.reason, state };
  const spent = { ...state.spent };
  delete spent[node.id];
  return {
    ok: true,
    state: {
      ...state,
      spent,
      refundsUsedThisVisit: state.refundsUsedThisVisit + 1,
    },
  };
}

/** Village rest resets the Trainer's refund allowance. */
export const onVillageRest = (state) => ({ ...state, refundsUsedThisVisit: 0 });

// ------------------------------------------------------------
// AFFINITY COMMITMENT — Caelarch's offer.
// One Affinity per character. Permanent. The other seven lock
// forever (a second purchased character can pick differently).
//
// ⚑ GATE-1: the tier check is GONE. The Doctrine names Affinity
// explicitly in the list of things no longer forbidden by a
// number next to your name (Part 2), and Part 6 states plainly
// that "Champion gated" now means "priced in Aenergy so that
// reaching it as a Beb is brutal," never a literal tier check.
//
// AFFINITY_COMMIT_REQ is that price. It is tuned to the
// ASCENSION LUMP rather than to the level number: every launch
// being clears it within a level or two of L30 on its own
// growth curve, without anything ever checking the tier. A
// Chaos Dragon (the largest Anima pool in the roster) can reach
// it about one level early — that is the Steering Law working
// as intended, not a leak.
//
// DIAL: one number. Raise it to push the oath deeper into
// Champion; lower it to let a determined Beb swear early and
// live with what it cost them.
// ------------------------------------------------------------
export const AFFINITY_COMMIT_REQ = { anima: 260 };

/**
 * @param {object} state
 * @param {string} affinityId
 * @param {{ level: number, beingId?: string, pool?: object }} ctx
 */
export function commitAffinity(state, affinityId, ctx = {}) {
  if (state.affinityId) return { ok: false, reason: "already_committed", state };
  const shortfall = aenergyShortfall(poolForContext(ctx), AFFINITY_COMMIT_REQ);
  if (shortfall)
    return { ok: false, reason: "aenergy_locked", shortfall, state };
  return { ok: true, state: { ...state, affinityId } };
}

// Kept exported so nothing that imported them breaks; the tier
// vocabulary is still real (the Ascent still happens, bodies
// still change at L30/L60) — it simply no longer gates.
export { TIERS, tierForLevel };
